import React, { useEffect, useRef, useState } from "react";

import { Grid, IconButton, Tooltip } from "@mui/material";
import { Check, Info, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { AiOutlineCamera } from "react-icons/ai";
import { FiEdit, FiTrash2 } from "react-icons/fi";

import { Destination } from "../../../../models/destination";
import { DifferentLocation as LocationAddress } from "../../../../models/location";
import { Material } from "../../../../models/material";
import { QuotedItem, RoomJob } from "../../../../models/sales";
import { Metric, handleGetConvertValue } from "../../../../util/converts";
import { DESTINATION } from "../../../../util/enums";
import getAllMaterialsFromRoom from "../../../../util/getAllMaterialsFromRoom";
import { getMeasurement, getWeightMeasurement } from "../../../../util/getMeasurement";
import { Colors } from "../../../Form/Button";
import Input from "../../../Form/Input";
import PicturesComponent from "../../PicturesComponent";
import CustomListComponent from "./CustomListComponent";
import useStyles from "./styles";

enum SumItemType {
  VOLUME = "volume",
  QUANTITY = "quantity",
}

interface RoomContentItemProps {
  room: RoomJob;
  destinations: Destination[];
  locations: LocationAddress[];
  isAllExpanded: boolean;
  viewOnly?: boolean;
  onCustomerView?: boolean;
  handleDeleteRoom: () => void;
  handleDeleteItem: (value: number) => void;
  handleDeleteMaterial: (value: number) => void;
  update: (value: QuotedItem, itemIndex: number) => void;
  updateMaterial: (material: Material, index: number) => void;
  updateRoom: (roomName: string) => void;
  handleAddPhoto: (pictures: string[]) => void;
  handleDeletePhoto: (pictureString?: string) => void;
  selectRoom: (isExpanded: boolean) => void;
}

const RoomContentItem = ({
  room,
  locations,
  destinations,
  isAllExpanded,
  viewOnly = false,
  onCustomerView = false,
  handleDeleteRoom,
  handleDeleteItem,
  handleDeleteMaterial,
  handleAddPhoto,
  handleDeletePhoto,
  update,
  updateMaterial,
  updateRoom,
  selectRoom,
}: RoomContentItemProps) => {
  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isUpdateRoomName, setIsUpdateRoomName] = useState<boolean>(false);
  const [roomName, setRoomName] = useState<string>(room.name);
  const inputfile = useRef<HTMLInputElement>(null);
  const { t: translate } = useTranslation();

  const classes = useStyles();

  const sumItensAndMaterials = (type: SumItemType.VOLUME | SumItemType.QUANTITY): number => {
    let materialSum = 0;
    const itemsSum = room.itemsJob
      ?.filter(
        (item) =>
          item?.itemJob?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
      )
      ?.reduce((total: number, item: QuotedItem) => total + Number(item?.itemJob[type]), 0);

    room?.materials?.forEach((material) => {
      if (
        material?.serviceBooking?.isItem &&
        material?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
      ) {
        materialSum += material.quantity;
      }
    });

    return Number(itemsSum) + Number(materialSum);
  };

  const totalVolume = () => {
    let itemsTotal = 0;
    let materialsTotal = 0;

    if (room.itemsJob.length > 0)
      itemsTotal = room.itemsJob
        ?.filter(
          (item) =>
            item?.itemJob?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
        )
        ?.reduce((total: number, item: QuotedItem) => {
          return (
            total +
            Number(
              handleGetConvertValue(
                Number(item?.itemJob?.volume),
                measurement?.code,
                measurement?.decimalPlaces
              )
            ) *
              Number(item?.itemJob?.quantity)
          );
        }, 0);
    if (room.materials.length > 0)
      materialsTotal = room.materials
        ?.filter(
          (material) =>
            material?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
        )
        ?.reduce(
          (total: number, material: Material) =>
            total +
            (material?.serviceBooking?.isItem
              ? Number(
                  handleGetConvertValue(
                    Number(material.volume),
                    measurement?.code,
                    measurement?.decimalPlaces
                  )
                ) * Number(material.quantity)
              : 0),
          0
        );

    return (itemsTotal + materialsTotal).toFixed(2);
  };

  const totalWeight = () => {
    let itemsTotal = 0;
    let materialsTotal = 0;

    if (room.itemsJob.length > 0) {
      itemsTotal = room.itemsJob
        ?.filter(
          (item) =>
            item?.itemJob?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
        )
        ?.reduce(
          (total: number, item: QuotedItem) =>
            total +
            Number(
              Number(
                handleGetConvertValue(
                  Number(item?.itemJob?.weight),
                  measurementWeight?.code || Metric.kg,
                  measurementWeight?.decimalPlaces
                )
              ) * Number(item?.itemJob?.quantity)
            ),
          0
        );
    }

    if (room?.materials?.length > 0) {
      materialsTotal = room?.materials
        ?.filter(
          (material) =>
            material?.destination?.name?.toLowerCase() !== DESTINATION.NOT_GOING?.toLowerCase()
        )
        ?.reduce(
          (total: number, material: Material) =>
            total +
            (material?.serviceBooking?.isItem
              ? Number(
                  handleGetConvertValue(
                    Number(material?.weight || 0),
                    measurementWeight?.code || Metric.kg,
                    measurementWeight?.decimalPlaces
                  )
                ) * Number(material.quantity)
              : 0),
          0
        );
    }

    return Number(itemsTotal + materialsTotal);
  };

  useEffect(() => {
    setIsExpanded(isAllExpanded);
  }, [isAllExpanded]);

  const totalItems = sumItensAndMaterials(SumItemType.QUANTITY);

  const renderRoomActions = () => {
    if (viewOnly) return null;

    return (
      <Grid item>
        <div className={classes.roomItemActionButtons}>
          {!isUpdateRoomName ? (
            <IconButton
              disabled={viewOnly}
              onClick={() => {
                setRoomName(room?.name || "");
                setIsUpdateRoomName(true);
              }}
            >
              <FiEdit color="#475467" />
            </IconButton>
          ) : (
            <IconButton
              disabled={viewOnly}
              onClick={() => {
                updateRoom(roomName);
                setIsUpdateRoomName(false);
              }}
            >
              <Check htmlColor="#475467" />
            </IconButton>
          )}
          <IconButton
            disabled={viewOnly}
            onClick={() => {
              if (!isExpanded) {
                setIsExpanded(!isExpanded);
                selectRoom(true);
              }
              inputfile?.current?.click();
            }}
          >
            <AiOutlineCamera fontSize="24px" color="#475467" />
          </IconButton>
          <IconButton disabled={viewOnly} onClick={() => handleDeleteRoom()}>
            <FiTrash2 color="#F97066" />
          </IconButton>
        </div>
      </Grid>
    );
  };

  const renderHeaderItem = () => {
    return (
      <Grid container direction="row" alignItems="center" justifyContent="space-between">
        <Grid item xs={4}>
          {isUpdateRoomName ? (
            <div className="d-flex align-items-center">
              <Input
                className={classes.input}
                name="roomName"
                value={roomName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setRoomName(event.target.value);
                }}
                autoFocus={isUpdateRoomName}
                disabled={viewOnly}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    updateRoom(roomName);
                    setIsUpdateRoomName(false);
                  }
                  if (event.key === "Escape") {
                    setRoomName("");
                    setIsUpdateRoomName(false);
                  }
                }}
              />
              <Tooltip title={String(translate("pressEnterToSaveOrEscToCancel"))} placement="right">
                <Info
                  htmlColor={Colors.SECONDARY.background.color}
                  fontSize="medium"
                  className={classes.ml12}
                />
              </Tooltip>
            </div>
          ) : (
            <span className={classes.roomItemName}>{room.name}</span>
          )}
        </Grid>
        <Grid
          item
          xs={8}
          container
          alignItems="center"
          justifyContent="flex-end"
          className={classes.gridItemActions}
        >
          {renderRoomActions()}
          <Grid item>
            <ul className={classes.roomItemInfo}>
              <li>
                <span className={classes.textInfoRoom}>
                  {totalItems}{" "}
                  {totalItems > 1
                    ? translate("items")?.toLowerCase()
                    : translate("item").toLowerCase()}
                </span>
              </li>
              <li>
                <span className={classes.textInfoRoom}>
                  {Number(totalVolume())} {measurement?.code}
                </span>
              </li>
              <li>
                {" "}
                <span className={classes.textInfoRoom}>
                  {totalWeight()}
                  {measurementWeight?.code || Metric.kg}
                </span>
              </li>
            </ul>
          </Grid>
          <Grid item>
            <IconButton
              onClick={() => {
                setIsExpanded(!isExpanded);
                selectRoom(!isExpanded);
              }}
            >
              {isExpanded ? (
                <KeyboardArrowUp htmlColor="#667085" />
              ) : (
                <KeyboardArrowDown htmlColor="#667085" />
              )}
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <div className="roomContentItemWrapper mb-5 pb-4 pr-2">
        <div className={classes.roomContentItemHeader}>{renderHeaderItem()}</div>

        {isExpanded && (
          <div className="roomContentListWrapper">
            <CustomListComponent
              items={room.itemsJob}
              locations={locations}
              destinations={destinations}
              materials={getAllMaterialsFromRoom(room)}
              viewOnly={viewOnly}
              onCustomerView={onCustomerView}
              deleteItem={(index: number) => handleDeleteItem(index)}
              deleteMaterial={(index: number) => handleDeleteMaterial(index)}
              updateItem={(item: QuotedItem, itemIndex: number) => update(item, itemIndex)}
              updateMaterial={(material: Material, materialIndex: number) =>
                updateMaterial(material, materialIndex)
              }
            />
          </div>
        )}
        <PicturesComponent
          ref={inputfile}
          classes="mt-3 pl-2"
          pictures={room.pictures || []}
          roomPictures={room.roomPictures}
          description={room.name}
          isExpanded={isExpanded}
          viewOnly={viewOnly}
          page="roomList"
          addPhoto={(photos: string[]) => handleAddPhoto(photos)}
          deletePhoto={(pictureIndex, uri) => handleDeletePhoto(uri)}
        />
      </div>
    </>
  );
};

export default RoomContentItem;
